import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Building2, Calendar, Users, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryResult } from "@/components/QueryResult";
import {
  StatusBadge,
  TypeBadges,
  ProjectStatus,
  ProjectType,
} from "@/components/ProjectBadges";
import { formatDateRange } from "@/lib/date-utils";
import {
  useProject,
  useProjectWriteAvailability,
  useUpdateZeroProject,
  useZeroProject,
} from "@/hooks/useProjects";
import { useLastOpenedProjects } from "@/hooks/useLastOpenedProjects";
import { useLastTabPath } from "@/hooks/useLastTabPath";
import { ProjectGeneralTab } from "@/components/project-detail/ProjectGeneralTab";
import { ProjectOverviewTab } from "@/components/project-detail/ProjectOverviewTab";
import { ProjectPlanTab } from "@/components/project-detail/ProjectPlanTab";
import { ProjectPeopleTab } from "@/components/project-detail/ProjectPeopleTab";
import { ProjectEquipmentTab } from "@/components/project-detail/ProjectEquipmentTab";
import { ProjectFinanceTab } from "@/components/project-detail/ProjectFinanceTab";
import { ProjectNotesTab } from "@/components/project-detail/ProjectNotesTab";
import { ProjectTasksTab } from "@/components/project-detail/ProjectTasksTab";
import { ProjectStreamsTab } from "@/components/project-detail/ProjectStreamsTab";
import { ProjectVehiclesTab } from "@/components/project-detail/ProjectVehiclesTab";
import { useSetCurrentEntity } from "@/contexts/RightSidebarContext";
import { usePageHeader } from "@/hooks/usePageHeader";
import { useMemo, useCallback } from "react";
import { formatProjectName } from "@/lib/format-project-name";
import { useAuth } from "@/hooks/useAuth";

const VALID_TABS = [
  "overview",
  "settings",
  "notes",
  "tasks",
  "finance",
  "plan",
  "equipment",
  "vehicles",
  "people",
  "streams",
] as const;
type TabValue = (typeof VALID_TABS)[number];

const PEOPLE_SUBTABS = [
  "overview",
  "availability",
  "functions",
  "planning",
  "intercom",
] as const;
type PeopleSubtab = (typeof PEOPLE_SUBTABS)[number];

export default function ProjectDetailPage() {
  const { id, "*": splat } = useParams<{ id: string; "*": string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, isError, error } = useProject(id);
  const { data: zeroProject } = useZeroProject(id);
  const updateProject = useUpdateZeroProject();
  const { canWrite, reason: writeUnavailableReason } =
    useProjectWriteAvailability();
  useSetCurrentEntity("project", id, project?.name);
  const { recordOpen } = useLastOpenedProjects();
  const { savePath, getPath } = useLastTabPath(id);

  useEffect(() => {
    if (project?.id) {
      recordOpen(project.id);
    }
  }, [project?.id, recordOpen]);

  // Parse tab and sub-paths from URL path segments
  const parts = (splat || "").split("/").filter(Boolean);
  const tabParam = parts[0] || "";
  const normalizedTabParam = tabParam === "meeting-notes" ? "notes" : tabParam;

  // Finance (quotes, invoices, time tracking) is employee-only.
  const { isEmployee } = useAuth();
  const currentTab: TabValue =
    VALID_TABS.includes(normalizedTabParam as TabValue) &&
    (normalizedTabParam !== "finance" || isEmployee)
      ? (normalizedTabParam as TabValue)
      : "overview";

  // Redirect bare plan/people tab roots to last visited sub-page (or default)
  useEffect(() => {
    if (
      (currentTab === "plan" || currentTab === "people") &&
      parts.length === 1
    ) {
      const defaultPath =
        currentTab === "plan"
          ? `/projects/${id}/plan/camera-plans`
          : `/projects/${id}/people/overview`;
      navigate(getPath(currentTab, defaultPath), { replace: true });
    }
  }, [currentTab, parts.length, id, navigate, getPath]);

  // Persist last visited sub-path for plan and people tabs
  useEffect(() => {
    if (
      (currentTab === "plan" || currentTab === "people") &&
      parts.length > 1
    ) {
      savePath(currentTab, `/projects/${id}/${splat}`);
    }
  }, [currentTab, parts.length, splat, id, savePath]);

  // People sub-tabs: /projects/:id/people/:subtab
  const currentPeopleSubtab: PeopleSubtab = PEOPLE_SUBTABS.includes(
    parts[1] as PeopleSubtab,
  )
    ? (parts[1] as PeopleSubtab)
    : "overview";

  // Finance sub-routes: /projects/:id/finance/quotes/:quoteId/versions/:versionId
  //                     /projects/:id/finance/invoices/:invoiceId
  const financeEntity = parts[1]; // "quotes" | "invoices" | undefined
  const financeEntityId = parts[2]; // quoteId or invoiceId
  const financeVersionId = parts[3] === "versions" ? parts[4] : undefined;

  // Plan sub-routes: /projects/:id/plan/camera-plans/:planId
  const planSubpath = parts[1]; // "camera-plans" | "network-coverage" | undefined
  const cameraPlanId = planSubpath === "camera-plans" ? parts[2] : undefined;

  const getTabHref = useCallback(
    (value: string) =>
      value === "overview" ? `/projects/${id}` : `/projects/${id}/${value}`,
    [id],
  );

  const handleTabChange = useCallback(
    (value: string) => {
      navigate(getTabHref(value));
    },
    [navigate, getTabHref],
  );

  const handlePeopleSubtabChange = useCallback(
    (value: string) => {
      navigate(`/projects/${id}/people/${value}`);
    },
    [navigate, id],
  );

  const headerTabs = useMemo(
    () =>
      [
        { value: "overview", label: "Overview" },
        { value: "settings", label: "Settings" },
        { value: "notes", label: "Notes" },
        { value: "tasks", label: "Tasks" },
        { value: "finance", label: "Finance" },
        { value: "plan", label: "Plan" },
        { value: "equipment", label: "Equipment" },
        { value: "vehicles", label: "Vehicles" },
        { value: "people", label: "People" },
        { value: "streams", label: "Streams" },
      ]
        .filter((tab) => tab.value !== "finance" || isEmployee)
        .map((tab) => ({ ...tab, href: getTabHref(tab.value) })),
    [getTabHref, isEmployee],
  );

  usePageHeader({
    title: project
      ? project.number
        ? `${project.number} – ${formatProjectName(project.name, project.event_start_date)}`
        : formatProjectName(project.name, project.event_start_date)
      : undefined,
    tabs: headerTabs,
    currentTab,
    onTabChange: handleTabChange,
  });

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!project) {
    return (
      <QueryResult
        isError={isError}
        error={error}
        data={project}
        entityName="project"
        backTo="/projects"
        backLabel="Back to Projects"
      >
        {null}
      </QueryResult>
    );
  }

  return (
    <div className="flex flex-col gap-6 animate-fade-in overflow-x-hidden max-w-full md:h-full">
      {/* Header */}
      <div className="flex flex-col gap-4 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            {project.number && (
              <span className="text-sm text-muted-foreground font-mono">
                {project.number}
              </span>
            )}
            {project.status && (
              <StatusBadge status={project.status as ProjectStatus} />
            )}
            {project.types && project.types.length > 0 && (
              <TypeBadges types={project.types as ProjectType[]} />
            )}
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              {project.companies?.name && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{project.companies.name}</span>
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4 flex-shrink-0" />
                {formatDateRange(
                  project.event_start_date,
                  project.event_end_date,
                )}
              </span>
              {project.project_groups?.name && project.project_group_id && (
                <Link
                  to={`/projects/groups/${project.project_group_id}`}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  <Users className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">
                    {project.project_groups.name}
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={currentTab}
        onValueChange={handleTabChange}
        className="w-full md:flex-1 md:min-h-0 md:flex md:flex-col"
      >
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="w-max min-w-full justify-start">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            {isEmployee && <TabsTrigger value="finance">Finance</TabsTrigger>}
            <TabsTrigger value="plan">Plan</TabsTrigger>
            <TabsTrigger value="equipment">Equipment</TabsTrigger>
            <TabsTrigger value="vehicles">Vehicles</TabsTrigger>
            <TabsTrigger value="people">People</TabsTrigger>
            <TabsTrigger value="streams">Streams</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent
          value="overview"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          <ProjectOverviewTab project={project} />
        </TabsContent>

        <TabsContent
          value="settings"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          {zeroProject && (
            <ProjectGeneralTab
              project={zeroProject}
              onUpdate={(updates) =>
                updateProject.mutate({ id: zeroProject.id, updates })
              }
              isUpdating={updateProject.isPending}
              isReadOnly={!canWrite}
              readOnlyReason={writeUnavailableReason}
            />
          )}
        </TabsContent>

        <TabsContent
          value="notes"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          <ProjectNotesTab project={project} />
        </TabsContent>

        <TabsContent
          value="tasks"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          <ProjectTasksTab project={project} />
        </TabsContent>

        {isEmployee && (
          <TabsContent
            value="finance"
            className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
          >
            <ProjectFinanceTab
              project={project}
              selectedQuoteId={
                financeEntity === "quotes" ? financeEntityId : undefined
              }
              selectedInvoiceId={
                financeEntity === "invoices" ? financeEntityId : undefined
              }
              selectedVersionId={financeVersionId}
              basePath={`/projects/${id}/finance`}
            />
          </TabsContent>
        )}

        <TabsContent
          value="plan"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          <ProjectPlanTab
            project={project}
            currentSubtab={planSubpath}
            selectedCameraPlanId={cameraPlanId}
            basePath={`/projects/${id}/plan`}
          />
        </TabsContent>

        <TabsContent
          value="equipment"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          <ProjectEquipmentTab project={project} />
        </TabsContent>

        <TabsContent
          value="vehicles"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          <ProjectVehiclesTab project={project} />
        </TabsContent>

        <TabsContent
          value="people"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          <ProjectPeopleTab
            project={project}
            currentSubtab={currentPeopleSubtab}
            onSubtabChange={handlePeopleSubtabChange}
          />
        </TabsContent>

        <TabsContent
          value="streams"
          className="mt-6 md:flex-1 md:min-h-0 md:overflow-y-auto"
        >
          <ProjectStreamsTab projectId={project.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
